import { CSSProperties, VFC } from 'react';
import { Route, Link, Routes, useLocation, useMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useTransition, a } from 'react-spring';
import { IoIosAddCircleOutline, IoIosArrowRoundForward } from 'react-icons/io';
import { API_FEED_LIST } from '../constants/apiConstants';
import { FLOW_PAGE_TITLE } from '../constants/pageConstants';
import { Page } from '../components/Page';
import { PageHeading } from '../components/styles/PageHeading';
import { ContentWrap } from '../components/styles/ContentWrap';
import { ViewFlow } from './ViewFlow';
import { AddFlow } from './AddFlow';
import { useAxios } from '../hooks/useAxios';
import { Spinner } from '../components/Spinner';
import { FeedList } from '../types';
import { ReactComponent as AddIcon } from '../static/add.svg';

const FlowList = styled.div`
    flex: 1 0 auto;
    width: 40%;
    min-width: 230px;
    max-width: ${props => props.theme.widths.innerSidebar};
    background: ${props => props.theme.colors.white};
    padding: ${props => props.theme.spacing[4]};
`;

interface FlowListItemProps {
    $addflow?: boolean;
}

const FlowListItem = styled(Link)<FlowListItemProps>`
    border: 0;
    display: block;
    padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[8]};
    cursor: pointer;
    position: relative;
    margin-bottom: ${props => props.theme.spacing[2]};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100px;
    background: ${props => props.$addflow ? props.theme.colors.primary[3] : props.theme.colors.white};
    color: ${props => props.$addflow ? props.theme.colors.white : props.theme.colors.primary[3]};
    border: ${props => props.$addflow ? 'none' : '2px solid' + props.theme.colors.primary[3]};
    box-shadow: ${props => props.theme.shadows.sm};
    transform: translateY(0);
    will-change: transform, box-shadow;
    transition: .25s ease;
    text-decoration: none;
    gap: ${props => props.theme.spacing[3]};
    stroke: ${props => props.$addflow ? props.theme.colors.white : props.theme.colors.primary[3]};
    font-weight: bold;
    font-family: adelle-sans;

    &:hover,
    &:focus,
    &:active {
        background-color: ${props => props.$addflow ? props.theme.colors.primary[4] : props.theme.colors.primary[3]};
        stroke: ${props => props.theme.colors.white};
        color: ${props => props.theme.colors.white};
    }
`;

const ButtonIcon = styled(IoIosArrowRoundForward)`
    position: absolute;
    right: ${props => props.theme.spacing[2]};
    top: 50%;
    transform: translateY(-50%);
    font-size: ${props => props.theme.fontSize[3]};
`;

const SingleFlowWrapper = styled.div`
    flex: 1 0 auto;
    width: 60%;
    overflow-x: hidden;
    position: relative;
`;

const SingleFlowPage = styled(a.div)`
    height: 100%;
    width: 100%;
    font-family: adelle-sans;
`;

const FlowEmptyState = styled.div`
    display: flex;
    height: 100%;
    justify-content: center;
    align-items: center;
    padding: 0 ${props => props.theme.spacing[7]};
`;

export const Flows: VFC = () => {
    const location = useLocation();
    const { data, isLoading, fetchData } = useAxios<FeedList>(API_FEED_LIST);
    const transitions = useTransition(location, {
        keys: loc => loc.pathname,
        from: {
            position: 'absolute' as CSSProperties['position'],
            opacity: 0,
            transform: 'translate3d(-15px,0,0)',
        },
        enter: {
            opacity: 1,
            transform: 'translate3d(0%,0,0)',
        },
        leave: {
            opacity: 0,
            transform: 'translate3d(-15px,0,0)',
        },
    });

    return (
        <Page>
            <PageHeading>{FLOW_PAGE_TITLE}</PageHeading>
            <ContentWrap>
                <FlowList>
                    <FlowListItem $addflow={true} to="add">
                        <AddIcon />
                        <span>Skapa nytt flöde</span>
                    </FlowListItem>
                    {isLoading ? (
                        <Spinner size={10} />
                    ) : data?.feeds?.map(item => (
                        <FlowListItem key={item.id} to={`${item.id}`}>
                            {item.name}
                            <ButtonIcon />
                        </FlowListItem>
                    ))}
                </FlowList>
                <SingleFlowWrapper>
                    {transitions((props, item) =>
                        <SingleFlowPage style={props}>
                            <Routes location={item}>
                                <Route
                                    path="add" 
                                    element={<AddFlow {...props} updateFeedList={fetchData} />}
                                />
                                <Route
                                    path=":flowId"
                                    element={<ViewFlow {...props} updateFeedList={fetchData} />}
                                />
                                <Route
                                    path="/"
                                    element={<FlowEmptyState>
                                        {isLoading || !data?.feeds?.length ? (
                                            <span>Välj ett existerande flöde, eller skapa ett nytt.</span>
                                        ) : (
                                            <span>Du har inga flöden, skapa ett flöde för att komma igång!</span>
                                        )}
                                    </FlowEmptyState>}
                                />
                            </Routes>
                        </SingleFlowPage>
                    )}
                </SingleFlowWrapper>
            </ContentWrap>
        </Page>
    );
}
